import { FC, useEffect, useState } from "react";
import DashboardLayout from "@/components/layouts/Dashboard";
import { apiGetDashboardData } from '@/services/HomeService';
import { DashboardData } from "@/@types/home";
import Skeleton from "@/components/ui/Skeleton";
import Card from "@/components/Card";

// Import all analytics components
import TotalCosts from "@/components/dashboard/TotalCosts";
import TestStats from "@/components/dashboard/TestStats";
import CodeReview from "@/components/dashboard/CodeReview";
import GeneralVue from "@/components/dashboard/GeneralVue";
import CostDetails from "@/components/dashboard/CostDetails";

// Import icons
import { 
  BarChart3, 
  TrendingUp, 
  PieChart, 
  DollarSign, 
  Code2, 
  Activity,
  Target,
  Zap,
  Clock
} from "lucide-react";

interface AnalyticsProps {}

const Analytics: FC<AnalyticsProps> = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const getDashboardData = () => {
    setLoading(true);
    apiGetDashboardData().then((response) => {
      setDashboardData(response.data);
    }).catch((_error) => {
      // Handle error silently
    }).finally(() => {
      setLoading(false);
    });
  };

  useEffect(() => {
    getDashboardData();
  }, []);

  // Calculate some additional metrics
  const getQuickStats = () => {
    if (!dashboardData) return null;

    const totalProjects = dashboardData.nbrProjects || 0;
    const totalShreds = dashboardData.shreddingCoins?.totalShreds || 0;
    const totalCredits = dashboardData.shreddingCoins?.totalCoins || 0;
    const activeShreds = dashboardData.shreddingCoins?.activeShreds || 0;

    return {
      totalProjects,
      totalShreds,
      totalCredits,
      activeShreds
    };
  };

  const quickStats = getQuickStats();

  const QuickStatsCard = ({ 
    icon: Icon, 
    title, 
    value, 
    subtitle, 
    color = "indigo" 
  }: { 
    icon: any, 
    title: string, 
    value: string | number, 
    subtitle: string,
    color?: string 
  }) => (
    <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="200px">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-3 bg-${color}-100 rounded-xl`}>
              <Icon className={`w-6 h-6 text-${color}-600`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">{title}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">{subtitle}</p>
      </div>
    </Card>
  );

  const SectionHeader = ({ 
    icon: Icon, 
    title, 
    description 
  }: { 
    icon: any, 
    title: string, 
    description: string 
  }) => (
    <div className="flex items-center space-x-3 mb-6">
      <div className="p-2 bg-indigo-100 rounded-lg">
        <Icon className="w-6 h-6 text-indigo-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="w-full max-w-7xl m-auto space-y-8">
        
        {/* Page Header */}
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 rounded-2xl p-8 text-white">
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
              <p className="text-indigo-100">Comprehensive insights into your development metrics and project performance</p>
            </div>
          </div>
          
          {/* Quick Overview Stats */}
          {!loading && quickStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{quickStats.totalProjects}</div>
                <div className="text-sm text-indigo-200">Total Projects</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{quickStats.totalShreds}</div>
                <div className="text-sm text-indigo-200">Total Shreds</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{quickStats.totalCredits.toLocaleString()}</div>
                <div className="text-sm text-indigo-200">Total Credits</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <div className="text-2xl font-bold">{quickStats.activeShreds}</div>
                <div className="text-sm text-indigo-200">Active Shreds</div>
              </div>
            </div>
          )}
        </div>

        {!loading ? (
          <>
            {/* Financial Analytics Section */}
            <div>
              <SectionHeader 
                icon={DollarSign}
                title="Financial Analytics"
                description="Track your spending, costs, and credit usage patterns"
              />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <TotalCosts shreddingCoins={dashboardData?.shreddingCoins} />
                </div>
                <div className="lg:col-span-2">
                  <CostDetails costDetailsData={dashboardData?.costDetails} />
                </div>
              </div>
            </div>

            {/* Project Performance Section */}
            <div>
              <SectionHeader 
                icon={TrendingUp}
                title="Project Performance"
                description="Monitor project progress and overall development metrics"
              />
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3">
                  <GeneralVue 
                    generalProgressData={dashboardData?.generalProgress}
                    shredsProgressData={dashboardData?.shredsProgress}
                  />
                </div>
                <div className="lg:col-span-1">
                  <CodeReview projectStatusData={dashboardData?.codeReviews} />
                </div>
              </div>
            </div>

            {/* Code Quality & Testing Section */}
            <div>
              <SectionHeader 
                icon={Code2}
                title="Code Quality & Testing"
                description="Analyze code metrics, test coverage, and quality indicators"
              />
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <TestStats shredsTestsData={dashboardData?.shredsTests} />
              </div>
            </div>

            {/* Additional Insights Section */}
            <div>
              <SectionHeader 
                icon={Activity}
                title="Development Insights"
                description="Key performance indicators and development trends"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {dashboardData?.shreddingCoins && (
                  <>
                    <QuickStatsCard
                      icon={Zap}
                      title="Avg Credits/Shred"
                      value={dashboardData.shreddingCoins.averageCoinsPerShred}
                      subtitle="Average credits spent per shred"
                      color="yellow"
                    />
                    <QuickStatsCard
                      icon={Target}
                      title="Max Credits/Shred"
                      value={dashboardData.shreddingCoins.maxCoinsForShred.toLocaleString()}
                      subtitle="Highest credits for single shred"
                      color="red"
                    />
                    <QuickStatsCard
                      icon={Clock}
                      title="Avg Generation Time"
                      value={`${dashboardData.shreddingCoins.generationTiming?.averageMinutes || 0}m`}
                      subtitle="Average time to generate"
                      color="blue"
                    />
                    <QuickStatsCard
                      icon={Activity}
                      title="Remaining Credits"
                      value={dashboardData.shreddingCoins.remainingCoins.toLocaleString()}
                      subtitle="Available credits balance"
                      color="green"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Status Breakdown */}
            {dashboardData?.shreddingCoins?.statusBreakdown && (
              <div>
                <SectionHeader 
                  icon={PieChart}
                  title="Project Status Breakdown"
                  description="Distribution of projects by current status"
                />
                
                <Card borderColor="[#dbe0f6]" bgColor="white" minHeight="200px">
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(dashboardData.shreddingCoins.statusBreakdown).map(([status, count]) => (
                        <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="text-2xl font-bold text-gray-900">{count}</div>
                          <div className="text-sm text-gray-600 capitalize">{status.replace('_', ' ')}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </>
        ) : (
          /* Loading Skeletons */
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Skeleton height={375} className="rounded-3xl" />
              <div className="lg:col-span-2">
                <Skeleton height={375} className="rounded-3xl" />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-3">
                <Skeleton height={400} className="rounded-3xl" />
              </div>
              <Skeleton height={400} className="rounded-3xl" />
            </div>
            <Skeleton height={300} className="rounded-3xl" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={120} className="rounded-3xl" />
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Analytics;